import React, { useRef, useState } from "react";
import { getLocalizedPath } from "../shared/formatURL";
import Link from "next/link";
import Image from "next/image";

interface Tag {
  _id: string;
  name: string;
}

export default function ListView({
  tagData,
  currStyle,
  translations,
  isLoading,
}: {
  tagData: any;
  currStyle: string;
  translations: any;
  isLoading: boolean;
}) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(true);
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftFade(scrollLeft > 0);
      setShowRightFade(scrollLeft < scrollWidth - clientWidth - 1); // -1 for rounding errors
    }
  };
  // Sort tags by name
  const sortedTags = [...(tagData as Tag[])].sort((a: Tag, b: Tag) =>
    a.name.localeCompare(b.name, translations.key, {
      sensitivity: "base",
      ignorePunctuation: true,
    }),
  );

  // Group tags by first character
  const groupedTags = sortedTags.reduce<Record<string, Tag[]>>((acc, tag) => {
    const name = (tag as Tag).name || "";
    const firstChar = name.charAt(0);

    // Use Unicode regex to check for any letter in any script
    // const isAlpha = /^[A-Za-z]$/.test(firstChar);
    const isLetter = /^\p{L}$/u.test(firstChar);
    const groupKey = isLetter ? firstChar.toUpperCase() : "#";

    if (!acc[groupKey]) acc[groupKey] = [];
    acc[groupKey].push(tag as Tag);

    return acc;
  }, {});

  // Sort group keys using localeCompare
  const sortedGroupKeys = Object.keys(groupedTags).sort((a, b) => {
    if (a === "#") return 1;
    if (b === "#") return -1;
    return a.localeCompare(b, translations.key);
  });

  const handleLetterClick = (letter: string) => {
    const element = document.getElementById(letter);
    // Scroll to letter if it exists
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const alphabet = [...Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i)), "#"];

  return (
    <div className="mx-auto px-5 xl:max-w-[88rem]">
      <div className="relative w-full overflow-hidden sm:px-0">
        <div
          ref={scrollContainerRef}
          className="flex items-center overflow-x-auto whitespace-nowrap scrollbar-hide md:justify-center"
          onScroll={handleScroll}
        >
          {/* A-Z indexing buttons */}
          <div className="mt-5 flex flex-row justify-center text-white md:flex-wrap">
            {alphabet.map((letter) => (
              <div key={letter}>
                <button
                  onClick={() => handleLetterClick(letter)}
                  className={`mr-2 whitespace-nowrap rounded-md px-1 py-[7px] text-sm font-medium text-white hover:text-crp-red hover:brightness-105 focus:outline-none focus:ring-0`}
                >
                  {letter}
                </button>
              </div>
            ))}
          </div>
        </div>
        {showLeftFade && (
          <div className="pointer-events-none absolute left-0 top-0 h-full w-20 bg-gradient-to-r from-[#202124] via-[#202124]/50 to-transparent sm:hidden"></div>
        )}
        {showRightFade && (
          <div className="pointer-events-none absolute right-0 top-0 h-full w-20 bg-gradient-to-l from-[#202124] via-[#202124]/50 to-transparent sm:hidden"></div>
        )}
      </div>

      {/* Divider */}
      <div className={`mb-10 mt-5 border-b border-grey-outline md:mx-6`}></div>
      {/* Loading Icon */}
      {isLoading && (
        <div className="flex">
          <Image
            src={"https://cdn6.imgpog.com/loadingGIF.webp"}
            width={50}
            height={50}
            alt="Loading"
            className="mx-auto mb-[55vh]"
            unoptimized
          />
        </div>
      )}

      {/* Grouped tags */}
      {sortedGroupKeys.map((letter, i) => (
        <div key={letter} className="mb-10 md:mx-6">
          <div className="grid grid-cols-5 items-start gap-4">
            {/* Letter header */}
            <h1 className="col-span-1 text-xl font-bold text-crp-red" id={letter}>
              {letter}
            </h1>
            {/* Grouped Taggs */}
            <ol className="col-span-4 grid grid-cols-2 gap-4 text-white md:grid-cols-4">
              {groupedTags[letter].map((tag) => (
                <li key={tag._id}>
                  <Link
                    prefetch={false}
                    href={getLocalizedPath(`tags/${tag._id}?style=${currStyle}&type=master`)}
                  >
                    {tag.name}
                  </Link>
                </li>
              ))}
            </ol>
          </div>
          {/* Divider */}
          <div
            className={`mt-10 border-b border-grey-outline ${
              i === sortedGroupKeys.length - 1 && "opacity-0"
            }`}
          ></div>
        </div>
      ))}
    </div>
  );
}
